import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Scalars, UserRole} from 'types';
import {useSearchParams} from 'react-router-dom';
import {useBoolean} from '@fluentui/react-hooks';
import {useCommand} from 'hooks';
import {
	InfinityList,
	InfinityListView,
} from 'components/InfinityList/InfinityList';
import {
	useGetexRegulationsQueryLazyQuery,
	useGetexRegulationsNextLazyQuery,
	useGetexSortOrdersLazyQuery,
} from './hooks/GetexRegulations.generated';
import {SelectionMode} from 'components/FluentUICompat';
import {useInfinityListStandardHooks} from 'components/InfinityList/InfinitListStandardHooks';
import {
	useGetexRegulationColumns,
	useGetexRegulationColumnMetaData,
	useGetexRegulationFilterOptions,
} from './GetexRegulationColumns';
import {SyncLatestDialog} from './SyncLatestDialog';

export type GetexAttachment = {
	attachmentId: string;
	file: {
		uri: string;
		fileName: string;
		originalFileName?: string | null;
	};
};

export type GetexRegulationModified = {
	id: Scalars['String'];
	regulationNumber: Scalars['String'];
	regulationTitle: Scalars['String'];
	regulationStatus: Scalars['String'];
	dateEffective?: string | null;
	keywords: Array<Scalars['String']>;
	vehicleCategories: Array<Scalars['String']>;
	attachments: Array<GetexAttachment>;
};

const initialSortField = 'regulationNumber';
const initialSortOrder = 'ASC';
const intBatchSize = 50;

const SyncLatestCommand: React.FC = () => {
	const {t} = useTranslation('features/getexregulation', {
		keyPrefix: 'GetexRegulationPage',
	});

	const [
		isSyncDialogOpen,
		{setTrue: openSyncDialog, setFalse: closeSyncDialog},
	] = useBoolean(false);

	useCommand(
		{
			key: 'syncLatest',
			text: t('SyncLatest'),
			iconProps: {iconName: 'Sync'},
			priority: 1,
			onClick() {
				openSyncDialog();
			},
			roles: [UserRole.SystemAdministrator, UserRole.Vko],
		},
		[],
	);

	return (
		<SyncLatestDialog isOpen={isSyncDialogOpen} onDismiss={closeSyncDialog} />
	);
};

const GetexRegulationPage: React.FC = () => {
	const {t} = useTranslation('features/getexregulation', {
		keyPrefix: 'GetexRegulationPage',
	});
	const {t: tViews} = useTranslation('features/getexregulation', {
		keyPrefix: 'Views',
	});

	const [getGetexRegulationsQuery, {loading: loadingQuery, data: dataQuery}] =
		useGetexRegulationsQueryLazyQuery();

	const [getGetexRegulationsNext, {loading: loadingDocuments}] =
		useGetexRegulationsNextLazyQuery();

	const [getGetexSortOrders] = useGetexSortOrdersLazyQuery();

	const [searchParams] = useSearchParams();

	const views: InfinityListView[] = useMemo(
		() => [{headerText: tViews('All'), accessKey: 'STD', query: ''}],
		[tViews],
	);

	const initialView =
		searchParams.getAll('view').length > 0
			? searchParams.getAll('view').at(0) ?? ''
			: 'STD';

	const arrIds = useMemo(
		() => dataQuery?.getexRegulationsQuery.getexIds ?? [],
		[dataQuery],
	);

	const columns = useGetexRegulationColumns();
	const columnMetaData = useGetexRegulationColumnMetaData();
	const columnFilterOptions = useGetexRegulationFilterOptions(dataQuery);

	const queryDocumentsDB = async (
		query: string,
		sortField: string,
		sortOrder: string,
	) => {
		const {data} = await getGetexRegulationsQuery({
			variables: {
				queryArgs: {query, sortField, sortOrder},
			},
		});

		const arrDocumentNext = (
			data?.getexRegulationsQuery.getexRegulationsBatch ?? []
		).map(x => funcMapGetexRegulation(x));
		const arrIDsNew = data?.getexRegulationsQuery.getexIds ?? [];
		return {arrDocumentNext, arrIDsNew};
	};

	const getDocumentsNextDB = async (
		preFilteredIds: string[],
	): Promise<GetexRegulationModified[]> => {
		const {data} = await getGetexRegulationsNext({
			variables: {nextArgs: {preFilteredIds}},
		});
		return (data?.getexRegulationsNext ?? []).map(x =>
			funcMapGetexRegulation(x),
		);
	};

	const getSortOrderDB = async (
		preFilteredIds: string[],
		sortField: string,
	): Promise<string[]> => {
		const {data} = await getGetexSortOrders({
			variables: {sortArgs: {preFilteredIds, sortField}},
		});
		return data?.getexSortOrders ?? preFilteredIds;
	};

	const {
		arrDocument,
		keyPrefix,
		linksFilters,
		firstRenderSTD,
		applyFilterSTD,
		applySortSTD,
		loadItemsNextSTD,
		excelExportSTD,
	} = useInfinityListStandardHooks<GetexRegulationModified>({
		views,
		intBatchSize,
		initialSortField,
		initialSortOrder,
		initialView,
		arrIds,
		columns,
		columnMetaData,
		queryDocumentsDB,
		getDocumentsNextDB,
		getSortOrderDB,
	});

	const handleInvokeItem = (_item: GetexRegulationModified) => {};
	const createHref = (item: unknown) =>
		`?id=${(item as GetexRegulationModified).id}`;

	const numberOfElements = arrIds.length ?? 1;

	return (
		<>
			<SyncLatestCommand />
			<InfinityList
				pageName={t('Title')}
				items={arrDocument}
				sticky={true}
				columns={columns}
				columnMetaData={columnMetaData}
				columnFilterOptions={columnFilterOptions}
				selectionMode={SelectionMode.none}
				stillLoading={loadingQuery || loadingDocuments}
				initalFilters={linksFilters}
				initialSortField={initialSortField}
				initialSortOrder={initialSortOrder}
				initialView={initialView}
				keyPrefix={keyPrefix}
				firstRender={firstRenderSTD}
				loadItemsNext={loadItemsNextSTD}
				applyFilter={applyFilterSTD}
				applySort={applySortSTD}
				excelExport={excelExportSTD}
				handleInvokeItem={handleInvokeItem}
				numberOfElements={numberOfElements}
				createHref={createHref}
				views={views}
			/>
		</>
	);
};

export const funcMapGetexRegulation = (item: {
	id: string;
	regulationNumber: string;
	regulationTitle: string;
	regulationStatus: string;
	dateEffective?: string | null;
	keywords: Array<string>;
	vehicleCategories: Array<string>;
	attachments?: Array<{
		attachmentId: string;
		file: {
			uri: string;
			fileName: string;
			originalFileName?: string | null;
		};
	}> | null;
}): GetexRegulationModified => ({
	id: item.id,
	regulationNumber: item.regulationNumber,
	regulationTitle: item.regulationTitle,
	regulationStatus: item.regulationStatus,
	dateEffective: item.dateEffective,
	keywords: item.keywords,
	vehicleCategories: item.vehicleCategories,
	attachments: item.attachments ?? [],
});

export default GetexRegulationPage;
